/**
 * Tasks API (tasks, comments, subtasks)
 */

import type { Task, Comment } from '../types';
import { fetchApi } from './fetch';

export async function getTask(taskId: string): Promise<Task> {
  return fetchApi<Task>(`/tasks/${taskId}`);
}

export async function deleteTask(taskId: string): Promise<void> {
  await fetchApi(`/tasks/${taskId}`, { method: 'DELETE' });
}

export async function completeTask(
  taskId: string,
  options?: { actualMinutes?: number; context?: string }
): Promise<{ taskId: string; completedAt: string }> {
  return fetchApi<{ taskId: string; completedAt: string }>(
    `/tasks/${taskId}/complete`,
    { method: 'POST', body: JSON.stringify(options || {}) }
  );
}

export async function reopenTask(taskId: string): Promise<Task> {
  return fetchApi<Task>(`/tasks/${taskId}/reopen`, { method: 'POST' });
}

export async function updateTask(
  taskId: string,
  updates: {
    content?: string;
    description?: string;
    priority?: number;
    dueString?: string;
    /** YYYY-MM-DD date string, or "" to clear */
    dueDate?: string;
    /** HH:MM time string (24-hour), or "" to clear time without clearing date */
    dueTime?: string;
    labels?: string[];
    category?: string;
    snoozeUntil?: string | null;
  }
): Promise<Task> {
  return fetchApi<Task>(`/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function createTask(data: {
  content: string;
  description?: string;
  parentId?: string;
  priority?: number;
  dueString?: string;
  /** YYYY-MM-DD date string */
  dueDate?: string;
  /** HH:MM time string (24-hour) */
  dueTime?: string;
  labels?: string[];
  projectId?: string;
  recurrenceRule?: string;
}): Promise<Task> {
  return fetchApi<Task>('/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function syncTasks(): Promise<{
  synced: number;
  tasks: number;
  duration: number;
}> {
  return fetchApi<{ synced: number; tasks: number; duration: number }>(
    '/tasks/sync',
    { method: 'POST' }
  );
}

export async function getTaskComments(taskId: string): Promise<{ data: Comment[]; total: number }> {
  return fetchApi<{ data: Comment[]; total: number }>(`/tasks/${taskId}/comments`);
}

export async function addTaskComment(taskId: string, content: string): Promise<Comment> {
  return fetchApi<Comment>(`/tasks/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

export async function updateTaskComment(taskId: string, commentId: string, content: string): Promise<Comment> {
  return fetchApi<Comment>(`/tasks/${taskId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ content }),
  });
}

export async function getSubtasks(taskId: string): Promise<Task[]> {
  const data = await fetchApi<{ tasks: Task[]; totalCount: number }>(
    `/tasks/${taskId}/subtasks`
  );
  return data.tasks || [];
}

export async function getSnoozedTasks(): Promise<{ data: Task[]; total: number }> {
  return fetchApi<{ data: Task[]; total: number }>('/tasks?snoozedOnly=true');
}

export async function getCompletedTasks(sortBy: 'completedAt' | 'createdAt' = 'completedAt'): Promise<{ data: Task[]; total: number }> {
  return fetchApi<{ data: Task[]; total: number }>(`/tasks?completedOnly=true&sortBy=${sortBy}&sortDirection=desc`);
}
